import { WA_WINDOW } from '@ng-web-apis/common';
import { TuiButton, TuiCheckbox, TuiInput, TuiTextfield } from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  output,
} from '@angular/core';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestService } from '@core/services/request/request.service';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  BehaviorSubject,
  delay,
  filter,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Nullable } from '@core/helpers/nullable';
import { AsyncPipe } from '@angular/common';
import { Position } from './types';
import { TranslatePipe } from '@ngx-translate/core';
import {
  PositionDefinerTranslations,
  PositionDefinerTranslationsPayload,
} from '@core/types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';

enum FormFields {
  Latitude = 'latitude',
  Longitude = 'longitude',
  IAmOnObjectPlace = 'iAmOnObjectPlace',
}

type AutoPositionSetStatus = {
  isSuccess: boolean;
  helpText: string;
};

@Component({
  selector: 'x-position-definer-form',
  imports: [
    TuiTextfield,
    AsyncPipe,
    ReactiveFormsModule,
    TuiInput,
    TuiButton,
    TuiCheckbox,
    TranslatePipe,
  ],
  templateUrl: './position-definer-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PositionDefinerFormComponent implements OnInit {
  private readonly requestService = inject(RequestService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly window = inject(WA_WINDOW);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly $request = this.requestService.$request;

  formCompleted = output<Nullable<Position>>();

  private translations: PositionDefinerTranslationsPayload = {
    [PositionDefinerTranslations.HelpTextSuccess]:
      'Координаты успешно определены',
    [PositionDefinerTranslations.HelpTextError]:
      'Не удалось определить координаты автоматически',
  };

  detectPositionAuto$ = new Subject<void>();

  autoPositionSetStatus$ = new BehaviorSubject<Nullable<AutoPositionSetStatus>>(
    null,
  );

  FormFields = FormFields;
  formGroup = this.formBuilder.group({
    [FormFields.Latitude]: ['', Validators.required],
    [FormFields.Longitude]: ['', Validators.required],
    [FormFields.IAmOnObjectPlace]: this.formBuilder.control<boolean>(false, {
      nonNullable: true,
    }),
  });

  get latitudeField() {
    return this.formGroup.controls[FormFields.Latitude];
  }

  get longitudeField() {
    return this.formGroup.controls[FormFields.Longitude];
  }

  get iAmOnObjectPlace() {
    return this.formGroup.controls[FormFields.IAmOnObjectPlace];
  }

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate(Object.values(PositionDefinerTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translations => (this.translations = translations));

    this.autoPositionSetStatus$
      .pipe(
        filter(Boolean),
        switchMap(() =>
          of(null).pipe(
            delay(3000),
            tap(() => {
              this.autoPositionSetStatus$.next(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.detectPositionAuto$
      .pipe(
        switchMap(() => this.getPositionAuto$()),
        tap(coords => {
          if (coords) {
            this.latitudeField.setValue(`${coords.latitude}`);
            this.longitudeField.setValue(`${coords.longitude}`);
            this.autoPositionSetStatus$.next({
              isSuccess: true,
              helpText:
                this.translations[PositionDefinerTranslations.HelpTextSuccess],
            });
          } else {
            this.autoPositionSetStatus$.next({
              isSuccess: false,
              helpText:
                this.translations[PositionDefinerTranslations.HelpTextError],
            });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getPositionAuto$(): Observable<GeolocationCoordinates | null> {
    return new Observable<GeolocationCoordinates | null>(observer => {
      this.window.navigator.geolocation.getCurrentPosition(
        position => {
          observer.next(position.coords);
          observer.complete();
        },
        () => {
          observer.next(null);
          observer.complete();
        },
      );
    });
  }

  cancelForm() {
    this.formCompleted.emit(null);
  }

  saveForm() {
    if (this.formGroup.valid) {
      const position: Position = {
        coords: {
          latitude: this.latitudeField.value!,
          longitude: this.longitudeField.value!,
        },
      };

      this.formCompleted.emit(position);
    }
  }
}
